import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, Phone, Mail, MapPin, Facebook, Linkedin, Twitter, Instagram } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = "/logo.png";

const centers = [
  { name: 'Centre de Formation Diplômante', path: '/diplomante', color: '#003366' },
  { name: 'Centre de Formation Qualifiante', path: '/qualifiante', color: '#DC143C' },
  { name: 'Centre de Formation Certifiante', path: '/certifiante', color: '#FFD700' },
  { name: 'Centre de Formation Linguistique', path: '/linguistique', color: '#8B5CF6' },
];

// Scroll to top function
const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    // Scroll to top on route change
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location]);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-lg' : 'bg-white/90 backdrop-blur-md'}`}>
      {/* Top bar */}
      <div className="bg-[#003366] text-white py-2 text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-6">
            <a href="tel:+2250712651719" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Phone size={14} />
              +225 07 12 65 17 19
            </a>
            <a href="tel:+2250100959518" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Phone size={14} />
              +225 01 00 95 95 18
            </a>
            <a href="mailto:cefat.bs@gmail.com" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Mail size={14} />
              cefat.bs@gmail.com
            </a>
            <a href="mailto:contact@cefat-bs.ci" className="flex items-center gap-2 hover:text-[#FFD700] transition-colors">
              <Mail size={14} />
              contact@cefat-bs.ci
            </a>
          </div>
          <div className="flex items-center gap-4">
            <a href="https://universitecefat.ci" target="_blank" rel="noopener noreferrer" className="hover:text-[#FFD700] transition-colors text-xs">
              Université CEFAT
            </a>
            <span className="text-white/50">|</span>
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <Facebook size={16} className="hover:text-[#FFD700] transition-colors" />
            </a>
            <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <Linkedin size={16} className="hover:text-[#FFD700] transition-colors" />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
              <Twitter size={16} className="hover:text-[#FFD700] transition-colors" />
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram size={16} className="hover:text-[#FFD700] transition-colors" />
            </a>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link" onClick={scrollToTop}>
            <img 
              src={LOGO_URL} 
              alt="CEFAT Business School" 
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="nav-link text-[#003366] font-medium hover:text-[#DC143C]" data-testid="nav-home" onClick={scrollToTop}>
              Accueil
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 nav-link text-[#003366] font-medium hover:text-[#DC143C] outline-none" data-testid="nav-centers-dropdown">
                Nos Centres <ChevronDown size={16} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white">
                {centers.map((center) => (
                  <DropdownMenuItem key={center.path} asChild>
                    <Link 
                      to={center.path} 
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                      data-testid={`nav-center-${center.path.slice(1)}`}
                      onClick={scrollToTop}
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: center.color }} />
                      {center.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/blog" className="nav-link text-[#003366] font-medium hover:text-[#DC143C]" data-testid="nav-blog" onClick={scrollToTop}>
              Blog
            </Link>
            
            <Link to="/a-propos" className="nav-link text-[#003366] font-medium hover:text-[#DC143C]" data-testid="nav-about" onClick={scrollToTop}>
              À propos
            </Link>
            
            <Link to="/contact" className="nav-link text-[#003366] font-medium hover:text-[#DC143C]" data-testid="nav-contact" onClick={scrollToTop}>
              Contact
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link to="/preinscription" onClick={scrollToTop}>
              <Button 
                className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-6 py-2 font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                data-testid="nav-preinscription-btn"
              >
                Je me préinscris
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button 
            className="lg:hidden p-2" 
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} className="text-[#003366]" /> : <Menu size={24} className="text-[#003366]" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 pb-4 border-t border-gray-100"
            >
              <div className="flex flex-col gap-4 pt-4">
                <Link to="/" className="text-[#003366] font-medium py-2">Accueil</Link>
                
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-500 mb-2">Nos Centres</p>
                  {centers.map((center) => (
                    <Link 
                      key={center.path} 
                      to={center.path}
                      className="flex items-center gap-3 py-2 text-[#003366]"
                    >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: center.color }} />
                      {center.name}
                    </Link>
                  ))}
                </div>

                <Link to="/blog" className="text-[#003366] font-medium py-2 border-t border-gray-100 pt-4">Blog</Link>
                <Link to="/a-propos" className="text-[#003366] font-medium py-2">À propos</Link>
                <Link to="/contact" className="text-[#003366] font-medium py-2">Contact</Link>
                
                <Link to="/preinscription" className="mt-4">
                  <Button className="w-full bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full py-3 font-semibold">
                    Je me préinscris
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export const Footer = () => {
  return (
    <footer className="bg-[#003366] text-white">
      {/* CTA Section */}
      <div className="bg-[#DC143C] py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Prêt à transformer votre avenir ?
          </h2>
          <p className="text-white/90 mb-6 text-lg">
            Rejoignez CEFAT Business School et ouvrez les portes de votre réussite professionnelle.
          </p>
          <Link to="/preinscription" onClick={scrollToTop}>
            <Button 
              className="bg-white text-[#DC143C] hover:bg-gray-100 rounded-full px-8 py-3 font-bold text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
              data-testid="footer-preinscription-btn"
            >
              Je me préinscris maintenant
            </Button>
          </Link>
        </div>
      </div>

      {/* Main Footer */}
      <div className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* About */}
            <div>
              <Link to="/" className="flex items-center gap-3 mb-6" onClick={scrollToTop}>
                <img 
                  src={LOGO_URL} 
                  alt="CEFAT Business School" 
                  className="h-14 w-auto object-contain bg-white p-2 rounded-lg"
                />
              </Link>
              <p className="text-white/70 leading-relaxed">
                Excellence académique et innovation pédagogique au service de votre réussite professionnelle en Afrique et dans le monde.
              </p>
            </div>

            {/* Centres */}
            <div>
              <h3 className="font-heading text-lg font-bold mb-6">Nos Centres</h3>
              <ul className="space-y-3">
                {centers.map((center) => (
                  <li key={center.path}>
                    <Link to={center.path} onClick={scrollToTop} className="footer-link flex items-center gap-2 hover:text-white transition-colors">
                      <div 
                        className="w-3 h-3 rounded-full border-2 border-white" 
                        style={{ backgroundColor: center.color }}
                      />
                      {center.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Liens rapides */}
            <div>
              <h3 className="font-heading text-lg font-bold mb-6">Liens rapides</h3>
              <ul className="space-y-3">
                <li><Link to="/a-propos" onClick={scrollToTop} className="footer-link hover:text-white transition-colors">À propos</Link></li>
                <li><Link to="/blog" onClick={scrollToTop} className="footer-link hover:text-white transition-colors">Blog & Actualités</Link></li>
                <li><Link to="/contact" onClick={scrollToTop} className="footer-link hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/preinscription" onClick={scrollToTop} className="footer-link hover:text-white transition-colors">Préinscription</Link></li>
                <li><Link to="/admin" onClick={scrollToTop} className="footer-link hover:text-white transition-colors">Espace Admin</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-heading text-lg font-bold mb-6">Contact</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin size={20} className="text-[#FFD700] flex-shrink-0 mt-1" />
                  <span className="text-white/70">Cocody Angré 8e Tranche<br />Carrefour Prière, Rue L259<br />Abidjan, Côte d'Ivoire</span>
                </li>
                <li>
                  <a href="tel:+2250712651719" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                    <Phone size={20} className="text-[#FFD700]" />
                    +225 07 12 65 17 19
                  </a>
                </li>
                <li>
                  <a href="tel:+2250100959518" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                    <Phone size={20} className="text-[#FFD700]" />
                    +225 01 00 95 95 18
                  </a>
                </li>
                <li>
                  <a href="mailto:cefat.bs@gmail.com" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                    <Mail size={20} className="text-[#FFD700]" />
                    cefat.bs@gmail.com
                  </a>
                </li>
                <li>
                  <a href="mailto:contact@cefat-bs.ci" className="flex items-center gap-3 text-white/70 hover:text-white transition-colors">
                    <Mail size={20} className="text-[#FFD700]" />
                    contact@cefat-bs.ci
                  </a>
                </li>
              </ul>
              <div className="mt-4 pt-4 border-t border-white/10">
                <p className="text-white/50 text-sm">Horaires d'ouverture</p>
                <p className="text-white/70 text-sm">Lun-Ven: 8h - 17h</p>
                <p className="text-white/70 text-sm">Sam: 8h - 12h</p>
              </div>
            </div>
          </div>

          {/* Social & Copyright */}
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-center md:text-left">
              <p className="text-white/50 text-sm">
                © {new Date().getFullYear()} CEFAT Business School. Tous droits réservés.
              </p>
              <p className="text-white/50 text-sm mt-1">
                BP 1238, Rue William Ipot2 (L84) • Membre du groupe <a href="https://universitecefat.ci" target="_blank" rel="noopener noreferrer" className="text-[#FFD700] hover:underline">Université CEFAT</a>
              </p>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Facebook">
                <Facebook size={18} />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="LinkedIn">
                <Linkedin size={18} />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Twitter">
                <Twitter size={18} />
              </a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Instagram">
                <Instagram size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-[120px] md:pt-[140px]">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
